import { FC, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Box, Button, Center, ShareOutline, SparklesOutline, Spinner, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useSpotlight } from '../../queries/spotlight';
import { useUser } from '../../queries/user';
import { SpotlightCard } from './spotlight-card';
import { SpotlightDebugBubbles } from './spotlight-debug-bubbles';
import { SpotlightNavigation } from './spotlight-navigation';

const BackgroundGradient = () => {
	const gradientId = useMemo(() => `paint0_linear_${Math.random().toString(36).substr(2, 9)}`, []);

	return (
		<Box
			position="fixed"
			top={0}
			left={0}
			width="100vw"
			height="100vh"
			zIndex={0}
			overflow="hidden"
			pointerEvents="none"
			bgGradient="linear(to-r, #D1FAE5, #FEF3C7)"
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 1213 920"
				preserveAspectRatio="xMidYMid slice"
				style={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: '100vw',
					height: '100vh',
					minWidth: '100vw',
					minHeight: '100vh',
				}}
			>
				<path
					d="M1146.6 415.325C1316.71 697.376 1116.16 984.089 994.617 1092.19C994.617 1092.19 589.215 1521.96 345.106 1438.04C100.996 1354.13 -569.564 527.855 -237.865 192.536C143.587 -193.081 933.964 62.7607 1146.6 415.325Z"
					fill={`url(#${gradientId})`}
				/>
				<defs>
					<linearGradient id={gradientId} x1="-63.0156" y1="38.4885" x2="1087.73" y2="1344.89" gradientUnits="userSpaceOnUse">
						<stop stopColor="white" stopOpacity="0.6" />
						<stop offset="0.461538" stopColor="white" stopOpacity="0.2" />
						<stop offset="1" stopColor="white" stopOpacity="0.4" />
					</linearGradient>
				</defs>
			</svg>
		</Box>
	);
};
interface SpotlightViewerProps {
	spotlightId: string;
}

export const SpotlightViewer: FC<SpotlightViewerProps> = ({ spotlightId }) => {
	const { data: spotlight, isLoading, error } = useSpotlight(spotlightId);
	const currentUser = useCurrentUser();
	const { data: user } = useUser(spotlight?.userId);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isViewingAsUser, setIsViewingAsUser] = useState(false);
	const [searchParams] = useSearchParams();
	const isDebugMode = searchParams.get('debug') === 'true';
	const actualIsViewingOwnSpotlight = spotlight?.userId === currentUser?.id;
	const isViewingOwnSpotlight = isViewingAsUser ? true : actualIsViewingOwnSpotlight;

	const handleNext = () => {
		if (currentIndex < (spotlight?.sections.length ?? 0)) {
			setCurrentIndex(currentIndex + 1);
		}
	};

	const handlePrevious = () => {
		if (currentIndex > 0) {
			setCurrentIndex(currentIndex - 1);
		}
	};

	const handleGoToCard = (index: number) => {
		setCurrentIndex(index);
	};

	const handleShare = async () => {
		console.log('share');
	};

	const renderContent = () => {
		if (isLoading || !spotlight) {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							Loading your spotlight...
						</Text>
					</VStack>
				</Center>
			);
		}

		if (error || spotlight.status === 'failed') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Text fontSize="xl" fontWeight="semibold" color="text.default">
							Unable to load spotlight
						</Text>
						<Text fontSize="md" color="text.secondary">
							{error instanceof Error ? error.message : 'An error occurred'}
						</Text>
					</VStack>
				</Center>
			);
		}

		if (spotlight.status === 'pending' || spotlight.status === 'processing') {
			return (
				<Center minH="100vh" position="relative" zIndex={1}>
					<VStack spacing={4}>
						<Spinner size="xl" color="success.icon" />
						<Text fontSize="lg" color="text.default">
							Generating your spotlight...
						</Text>
					</VStack>
				</Center>
			);
		}

		const totalCards = spotlight.sections.length + 1; // +1 for final card
		const isFinalCard = currentIndex === spotlight.sections.length;

		return (
			<>
				{/* Progress bar */}
				<Box
					position="fixed"
					top={0}
					left={0}
					h="3px"
					bg="linear-gradient(90deg, #1DB87C, #7C5CFC, #FF6B9D)"
					width={`${((currentIndex + 1) / totalCards) * 100}%`}
					transition="width 0.4s ease"
					zIndex={100}
				/>

				{/* Debug: User name (only if viewing someone else's spotlight) */}
				{isDebugMode && !actualIsViewingOwnSpotlight && user?.displayName && (
					<Box
						as="button"
						position="fixed"
						top={4}
						right={4}
						zIndex={101}
						px={4}
						py={2}
						bg="text.default"
						color="text.onColor"
						borderRadius="md"
						fontSize="sm"
						fontWeight="semibold"
						cursor="pointer"
						onClick={() => setIsViewingAsUser(!isViewingAsUser)}
						_hover={{
							opacity: 0.9,
						}}
						maxW={{ base: 'calc(100% - 32px)', md: 'auto' }}
					>
						{isViewingAsUser
							? `Viewing as ${user.displayFirstName || user.displayName}...`
							: `You are viewing ${user.displayFirstName || user.displayName}'s spotlight`}
					</Box>
				)}

				{/* Main content */}
				<Box
					maxW="900px"
					mx="auto"
					px={{ base: 6, md: 12 }}
					pb={{ base: 32, md: 12 }}
					minH="100vh"
					display="flex"
					flexDirection="column"
					justifyContent={{ base: 'flex-start', md: 'center' }}
					pt={{ base: isDebugMode && !actualIsViewingOwnSpotlight ? 20 : 8, md: 0 }}
					position="relative"
					zIndex={1}
					overflowY={{ base: 'auto', md: 'visible' }}
				>
					<Box position="relative" width="100%" minH={{ base: 'auto', md: '750px' }} overflow="visible">
						{/* Regular cards */}
						{spotlight.sections.map((section, index) => (
							<Box key={index} position="relative">
								<SpotlightCard section={section} isActive={currentIndex === index} />
								{isDebugMode && <SpotlightDebugBubbles section={section} sectionIndex={index} isActive={currentIndex === index} />}
							</Box>
						))}

						{/* Final card */}
						{isFinalCard && (
							<Box position="absolute" top={0} left={0} right={0} opacity={1} visibility="visible" pointerEvents="auto">
								<VStack spacing={6} align="stretch">
									<Box textAlign="center">
										<Box
											as="span"
											display="inline-block"
											px={4}
											py={2}
											borderRadius="full"
											fontSize="xs"
											fontWeight="semibold"
											textTransform="uppercase"
											letterSpacing="wide"
											mb={4}
											bg="success.bg"
											color="success.text"
											border="1px solid"
											borderColor="border.soft"
											boxShadow="0 2px 8px rgba(0, 0, 0, 0.08)"
										>
											The End
										</Box>
										<Text
											fontFamily="'Plus Jakarta Sans', sans-serif"
											fontSize="lg"
											fontWeight="medium"
											color="text.default"
											opacity={0.7}
											textTransform="uppercase"
											letterSpacing="wide"
										>
											{isViewingOwnSpotlight ? "That's a wrap on your highlights." : "That's a wrap on their highlights."}
										</Text>
									</Box>

									<Box
										bg="bg.surface"
										backdropFilter="blur(20px)"
										borderRadius="3xl"
										p={{ base: 12, md: 16 }}
										border="2px solid"
										borderColor="bg.topNav"
										boxShadow="0 8px 32px rgba(0, 0, 0, 0.08)"
										textAlign="center"
									>
										<VStack spacing={5}>
											<Box w={16} h={16} borderRadius="2xl" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={5}>
												<SparklesOutline size="lg" color="success.text" />
											</Box>
											<Text
												as="h2"
												fontFamily="'Plus Jakarta Sans', sans-serif"
												fontSize={{ base: '3xl', md: '4xl' }}
												fontWeight="bold"
												lineHeight="shorter"
												color="text.default"
											>
												{isViewingOwnSpotlight ? "You're Kind of Amazing" : 'Share This Spotlight'}
											</Text>
											<Text fontSize="md" color="text.secondary" mb={7}>
												{isViewingOwnSpotlight
													? 'Now go share your wins with the world.'
													: `Share this spotlight to celebrate ${user?.displayFirstName ? `${user.displayFirstName}'s` : 'their'} achievements.`}
											</Text>
											<Button
												leftIcon={<ShareOutline size="md" />}
												onClick={handleShare}
												bg="success.icon"
												color="text.onColor"
												size="md"
												borderRadius="full"
												px={5}
												py={3}
												fontSize="sm"
												fontWeight="semibold"
												isDisabled
												_hover={{
													bg: 'success.text',
													transform: 'scale(1.02)',
												}}
											>
												Share
											</Button>
										</VStack>
									</Box>
								</VStack>
							</Box>
						)}
					</Box>

					{/* Navigation */}
					<SpotlightNavigation
						currentIndex={currentIndex}
						totalCards={totalCards}
						onPrevious={handlePrevious}
						onNext={handleNext}
						onGoToCard={handleGoToCard}
					/>
				</Box>
			</>
		);
	};

	return (
		<Box minH="100vh" bg="bg.surface" position="relative">
			<BackgroundGradient />
			{renderContent()}
		</Box>
	);
};
